import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useParams, useBlocker } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  Flag,
  RotateCcw,
  Send,
  Grid3X3,
  Menu,
  X,
  Loader2,
} from 'lucide-react'
import TestTimer from '../components/TestTimer'
import TestQuestion from '../components/TestQuestion'
import QuestionPalette from '../components/QuestionPalette'
import SubmitConfirmModal from '../components/SubmitConfirmModal'
import {
  useTestAttempt,
  useQuestionsBatch,
  useUpdateQuestionResponse,
  useSubmitTestAttempt,
  useAbandonTestAttempt,
} from '../hooks/useMockTest'
import { useEventTracking } from '../hooks/useEventTracking'
import { useCompleteAssessment } from '@/features/test-series/hooks'
import {
  trackTestSubmit,
  trackTestTimeout,
  trackError,
  trackQuickTestComplete,
  trackFullTestComplete,
} from '@/shared/utils/analytics'
import { fbTrackTestComplete, fbTrackTestTimeout } from '@/shared/utils/facebookPixel'
import type { ResponseStatus, TestQuestionWithContent } from '../types'

export default function MockTestPage() {
  const navigate = useNavigate()
  const { testId, attemptId } = useParams<{ testId: string; attemptId: string }>()

  // UI State
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showPalette, setShowPalette] = useState(true)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Question responses state (local state for fast UI updates)
  const [responses, setResponses] = useState<
    Map<string, { selectedAnswer: string | null; isMarkedForReview: boolean; status: ResponseStatus }>
  >(new Map())

  // Fetch attempt details
  const { data: attemptData, isLoading: isLoadingAttempt, error: attemptError } = useTestAttempt(attemptId || '')

  // Fetch complete assessment (with sections and questions)
  const { data: assessmentData, isLoading: isLoadingAssessment, error: assessmentError } = useCompleteAssessment(testId || '')
  const assessment = assessmentData?.data

  // Event tracking hook
  const eventTracking = useEventTracking({
    attemptId: attemptId || '',
  })

  // Flatten questions from all sections and collect question IDs
  const testQuestions = useMemo(() => {
    if (!assessment?.sections) return []

    const questions: Array<{
      questionMappingId: string
      questionId: string
      questionNumber: number
      sectionId: string
      sectionName: string
      marks: number
      negativeMarks: number
    }> = []

    let questionNumber = 1
    for (const section of assessment.sections) {
      for (const q of section.questions || []) {
        questions.push({
          questionMappingId: q.questionMappingId,
          questionId: q.questionId,
          questionNumber: questionNumber++,
          sectionId: section.sectionId,
          sectionName: section.sectionName,
          marks: q.marks,
          negativeMarks: q.negativeMarks,
        })
      }
    }

    return questions
  }, [assessment])

  // Get all question IDs for batch fetch
  const questionIds = useMemo(
    () => testQuestions.map((q) => q.questionId),
    [testQuestions]
  )

  // Fetch questions content from question-bank
  const { data: questionsData, isLoading: isLoadingQuestions, error: questionsError } = useQuestionsBatch(questionIds)

  // Mutations
  const updateResponse = useUpdateQuestionResponse(attemptId || '')
  const submitTest = useSubmitTestAttempt()
  const abandonTest = useAbandonTestAttempt()

  // Track if test has been submitted (to avoid abandoning after submit)
  const isSubmittedRef = useRef(false)

  // Build questions with content
  const questions: TestQuestionWithContent[] = useMemo(() => {
    if (!questionsData?.data?.questions || testQuestions.length === 0) return []

    const questionsMap = new Map(
      questionsData.data.questions.map((q) => [q.questionId, q])
    )

    return testQuestions.map((tq) => {
      const content = questionsMap.get(tq.questionId)
      const response = responses.get(tq.questionMappingId)

      return {
        testQuestionId: tq.questionMappingId,
        questionId: tq.questionId,
        questionNumber: tq.questionNumber,
        sectionId: tq.sectionId,
        sectionName: tq.sectionName,
        marks: tq.marks,
        negativeMarks: tq.negativeMarks,
        questionText: content?.questionData?.text || 'Loading...',
        options: content?.questionData?.options || [],
        testCode: content?.testCode,
        difficultyLevel: content?.difficultyLevel || 'MEDIUM',
        status: response?.status || 'UNSEEN',
        selectedAnswer: response?.selectedAnswer,
        isMarkedForReview: response?.isMarkedForReview || false,
        timeSpentMs: 0,
      }
    })
  }, [questionsData, testQuestions, responses])

  const currentQuestion = questions[currentIndex]
  const totalQuestions = questions.length

  // Track previous question index for navigation events
  const prevIndexRef = useRef<number | null>(null)
  const hasInitializedRef = useRef(false)

  // Handle question navigation with event tracking
  useEffect(() => {
    if (questions.length === 0 || !eventTracking.isInitialized) return

    const currentQ = questions[currentIndex]
    if (!currentQ) return

    // Close previous question if navigating
    if (prevIndexRef.current !== null && prevIndexRef.current !== currentIndex) {
      eventTracking.closeQuestion()
    }

    // Open current question
    if (prevIndexRef.current !== currentIndex || !hasInitializedRef.current) {
      eventTracking.openQuestion(currentQ.questionId, currentQ.testQuestionId)
      hasInitializedRef.current = true
    }

    prevIndexRef.current = currentIndex
  }, [currentIndex, questions, eventTracking])

  // Mark question as seen when navigating
  useEffect(() => {
    if (!currentQuestion) return

    setResponses((prev) => {
      const existing = prev.get(currentQuestion.testQuestionId)
      if (existing?.status !== 'UNSEEN') return prev

      const updated = new Map(prev)
      updated.set(currentQuestion.testQuestionId, {
        selectedAnswer: existing?.selectedAnswer || null,
        isMarkedForReview: existing?.isMarkedForReview || false,
        status: 'SEEN',
      })
      return updated
    })
  }, [currentQuestion?.testQuestionId])

  // Handle scroll events for guessed flag detection
  const handleScroll = useCallback(() => {
    eventTracking.recordScroll()
  }, [eventTracking])

  // Handle answer selection
  const handleSelectAnswer = useCallback(
    (option: string) => {
      if (!currentQuestion || !attemptId) return

      const existing = responses.get(currentQuestion.testQuestionId)
      const previousAnswer = existing?.selectedAnswer
      const newAnswer = previousAnswer === option ? null : option

      if (newAnswer) {
        // Track answer selection
        eventTracking.selectAnswer(option)
      } else {
        // Track answer clear (when clicking same option to deselect)
        if (previousAnswer) {
          eventTracking.clearAnswer()
        }
      }

      // Update local state immediately
      setResponses((prev) => {
        const updated = new Map(prev)
        updated.set(currentQuestion.testQuestionId, {
          selectedAnswer: newAnswer,
          isMarkedForReview: existing?.isMarkedForReview || false,
          status: newAnswer ? 'ATTEMPTED' : 'SEEN',
        })
        return updated
      })

      // Sync with server
      updateResponse.mutate({
        testQuestionId: currentQuestion.testQuestionId,
        questionId: currentQuestion.questionId,
        selectedAnswer: newAnswer,
        status: newAnswer ? 'ATTEMPTED' : 'SEEN',
        isMarkedForReview: existing?.isMarkedForReview || false,
      })
    },
    [currentQuestion, responses, attemptId, updateResponse, eventTracking]
  )

  // Handle mark for review
  const handleMarkForReview = useCallback(() => {
    if (!currentQuestion || !attemptId) return

    const existing = responses.get(currentQuestion.testQuestionId)
    const newMarkedState = !existing?.isMarkedForReview

    // Track event
    if (newMarkedState) {
      eventTracking.markForReview()
    } else {
      eventTracking.unmarkForReview()
    }

    setResponses((prev) => {
      const updated = new Map(prev)
      updated.set(currentQuestion.testQuestionId, {
        selectedAnswer: existing?.selectedAnswer || null,
        isMarkedForReview: newMarkedState,
        status: existing?.status || 'SEEN',
      })
      return updated
    })

    updateResponse.mutate({
      testQuestionId: currentQuestion.testQuestionId,
      questionId: currentQuestion.questionId,
      selectedAnswer: existing?.selectedAnswer,
      isMarkedForReview: newMarkedState,
      status: existing?.status || 'SEEN',
    })
  }, [currentQuestion, responses, attemptId, updateResponse, eventTracking])

  // Handle clear response
  const handleClearResponse = useCallback(() => {
    if (!currentQuestion || !attemptId) return

    const existing = responses.get(currentQuestion.testQuestionId)

    // Track event
    eventTracking.clearAnswer()

    setResponses((prev) => {
      const updated = new Map(prev)
      updated.set(currentQuestion.testQuestionId, {
        selectedAnswer: null,
        isMarkedForReview: existing?.isMarkedForReview || false,
        status: 'SEEN',
      })
      return updated
    })

    updateResponse.mutate({
      testQuestionId: currentQuestion.testQuestionId,
      questionId: currentQuestion.questionId,
      selectedAnswer: null,
      status: 'SEEN',
      isMarkedForReview: existing?.isMarkedForReview || false,
    })
  }, [currentQuestion, responses, attemptId, updateResponse, eventTracking])

  // Navigation
  const goToPrev = () => setCurrentIndex((i) => Math.max(0, i - 1))
  const goToNext = () => setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1))
  const goToQuestion = (index: number) => {
    setCurrentIndex(index)
    setShowPalette(false)
    setIsMobileMenuOpen(false)
  }

  // Submit test
  const handleSubmit = async () => {
    if (!attemptId) return

    try {
      // Close current question before submit
      eventTracking.closeQuestion()

      await submitTest.mutateAsync(attemptId)

      // Mark as submitted to prevent abandon on navigation
      isSubmittedRef.current = true

      // Track test submission
      trackTestSubmit(
        assessment?.testCode || '',
        0, // Score not available at submission time
        totalQuestions,
        summaryStats.answered
      )
      fbTrackTestComplete(
        assessment?.testCode || '',
        0,
        totalQuestions,
        summaryStats.answered
      )

      // Track specific test type completion
      if (assessment?.testType === 'QUICK_DIAGNOSTIC') {
        trackQuickTestComplete()
      } else if (assessment?.testType === 'FULL_LENGTH') {
        trackFullTestComplete()
      }

      // Navigate to results page
      navigate(`/mock-test/${testId}/results/${attemptId}`)
    } catch (error) {
      console.error('Failed to submit test:', error)
      trackError('test_submit', error instanceof Error ? error.message : 'Failed to submit test', assessment?.testCode)
    }
  }

  // Time up handler
  const handleTimeUp = useCallback(async () => {
    if (attemptId) {
      // Close current question
      eventTracking.closeQuestion()

      // Track timeout event
      trackTestTimeout(assessment?.testCode || '')
      fbTrackTestTimeout(assessment?.testCode || '')

      try {
        // Wait for submission to complete before navigating
        await submitTest.mutateAsync(attemptId)
        // Mark as submitted to prevent abandon on navigation
        isSubmittedRef.current = true
        // Navigate to results page after timeout
        navigate(`/mock-test/${testId}/results/${attemptId}`)
      } catch (error) {
        console.error('Failed to auto-submit test on timeout:', error)
        // Mark as submitted anyway to prevent abandon
        isSubmittedRef.current = true
        // Still navigate to results - the backend may have already processed it
        navigate(`/mock-test/${testId}/results/${attemptId}`)
      }
    }
  }, [attemptId, testId, submitTest, navigate, assessment?.testCode, eventTracking])

  // Handle browser close/refresh - warn user and abandon test
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isSubmittedRef.current && attemptId) {
        // Show browser's default "are you sure" dialog
        e.preventDefault()
        e.returnValue = ''

        // Try to abandon the test (fire and forget - sendBeacon would be better for reliability)
        abandonTest.mutate(attemptId)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [attemptId, abandonTest])

  // Block navigation away from test (back button, link clicks, etc.)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isSubmittedRef.current &&
      currentLocation.pathname !== nextLocation.pathname &&
      !nextLocation.pathname.includes('/results/')
  )

  // Handle navigation block - show confirmation or abandon
  useEffect(() => {
    if (blocker.state === 'blocked' && attemptId) {
      const confirmLeave = window.confirm(
        'You have an ongoing test. If you leave, your test will be marked as ABANDONED and you may not be able to resume it.\n\nAre you sure you want to leave?'
      )

      if (confirmLeave) {
        // Abandon the test
        abandonTest.mutate(attemptId)
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker, attemptId, abandonTest])

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    let answered = 0
    let markedForReview = 0

    responses.forEach((r) => {
      if (r.selectedAnswer) answered++
      if (r.isMarkedForReview) markedForReview++
    })

    return {
      answered,
      unanswered: totalQuestions - answered,
      markedForReview,
    }
  }, [responses, totalQuestions])

  // Error state
  const hasError = attemptError || assessmentError || questionsError
  if (hasError) {
    const errorMessage = attemptError?.message || assessmentError?.message || questionsError?.message || 'Failed to load test'
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Test</h2>
          <p className="text-gray-600 mb-4">{errorMessage}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  // Loading state
  if (isLoadingAttempt || isLoadingAssessment || isLoadingQuestions) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading test...</p>
        </div>
      </div>
    )
  }

  // No questions state - assessment loaded but no questions found
  if (!currentQuestion) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-yellow-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Questions Found</h2>
          <p className="text-gray-600 mb-2">This test doesn't have any questions configured.</p>
          <p className="text-sm text-gray-500 mb-4">
            Test: {testId} | Attempt: {attemptId}<br />
            Sections: {assessment?.sections?.length || 0} |
            Questions: {testQuestions.length}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const response = responses.get(currentQuestion.testQuestionId)

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-3 py-2 z-20">
        <div className="flex items-center justify-between">
          {/* Left: Menu button (mobile) and test name */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="hidden sm:block">
              <h1 className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">
                {assessment?.testName || 'Mock Test'}
              </h1>
            </div>
          </div>

          {/* Center: Timer */}
          <TestTimer
            durationMinutes={assessment?.durationMinutes || 180}
            startTime={new Date(attemptData?.data?.startedAt || Date.now())}
            onTimeUp={handleTimeUp}
          />

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPalette(!showPalette)}
              className="hidden lg:flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Grid3X3 className="w-4 h-4" />
              <span className="hidden xl:inline">Questions</span>
            </button>
            <button
              onClick={() => setShowSubmitModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Submit</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative">
        {/* Mobile Palette Overlay */}
        {isMobileMenuOpen && (
          <div className="absolute inset-0 z-10 lg:hidden">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl">
              <QuestionPalette
                questions={questions.map((q) => ({
                  questionNumber: q.questionNumber,
                  status: responses.get(q.testQuestionId)?.status || 'UNSEEN',
                  isMarkedForReview: responses.get(q.testQuestionId)?.isMarkedForReview || false,
                  hasAnswer: !!responses.get(q.testQuestionId)?.selectedAnswer,
                  sectionId: q.sectionId,
                  sectionName: q.sectionName,
                }))}
                currentIndex={currentIndex}
                onSelectQuestion={goToQuestion}
                showCloseButton
                onClose={() => setIsMobileMenuOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Question Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TestQuestion
            questionNumber={currentQuestion.questionNumber}
            totalQuestions={totalQuestions}
            questionText={currentQuestion.questionText}
            options={currentQuestion.options}
            testCode={currentQuestion.testCode}
            selectedAnswer={response?.selectedAnswer}
            onSelectAnswer={handleSelectAnswer}
            onScroll={handleScroll}
            difficultyLevel={currentQuestion.difficultyLevel}
            sectionName={currentQuestion.sectionName}
          />
        </div>

        {/* Desktop Sidebar Palette */}
        {showPalette && (
          <div className="hidden lg:block w-72 border-l border-gray-200 bg-white">
            <QuestionPalette
              questions={questions.map((q) => ({
                questionNumber: q.questionNumber,
                status: responses.get(q.testQuestionId)?.status || 'UNSEEN',
                isMarkedForReview: responses.get(q.testQuestionId)?.isMarkedForReview || false,
                hasAnswer: !!responses.get(q.testQuestionId)?.selectedAnswer,
                sectionId: q.sectionId,
                sectionName: q.sectionName,
              }))}
              currentIndex={currentIndex}
              onSelectQuestion={goToQuestion}
            />
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <footer className="flex-shrink-0 bg-white border-t border-gray-200 px-3 py-2 z-20">
        <div className="flex items-center justify-between gap-2">
          {/* Left actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleMarkForReview}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                response?.isMarkedForReview
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Flag className="w-4 h-4" />
              <span className="hidden sm:inline">
                {response?.isMarkedForReview ? 'Marked' : 'Mark'}
              </span>
            </button>
            <button
              onClick={handleClearResponse}
              disabled={!response?.selectedAnswer}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          </div>

          {/* Mobile palette button */}
          <button
            onClick={() => setShowPalette(!showPalette)}
            className="lg:hidden flex items-center gap-1 px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg"
          >
            <Grid3X3 className="w-4 h-4" />
            <span>{currentIndex + 1}/{totalQuestions}</span>
          </button>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrev}
              disabled={currentIndex === 0}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Prev</span>
            </button>
            <button
              onClick={goToNext}
              disabled={currentIndex >= totalQuestions - 1}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </footer>

      {/* Submit Confirmation Modal */}
      <SubmitConfirmModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onConfirm={handleSubmit}
        totalQuestions={totalQuestions}
        answeredCount={summaryStats.answered}
        unansweredCount={summaryStats.unanswered}
        markedForReviewCount={summaryStats.markedForReview}
        isSubmitting={submitTest.isPending}
      />
    </div>
  )
}
